import React, { useEffect, useState } from "react";

import api, { IMAGE_BASE_URL } from "../api/Api.js";

import printImage from "../assets/images/print.png";
import newReservationImage from "../assets/images/newreservation.png";
import tickImage from "../assets/images/tickm.png";

import { useNavigate, useLocation } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import { useLanguage } from "../context/LanguageContext";

function ConfirmBooking() {
  const navigate = useNavigate();
  const location = useLocation();

  // ==========================================================
  // LANGUAGE
  // ==========================================================

  const { language, t } = useLanguage();

  const languageId = Number(
    language?.lang_id ||
      language?.language_id ||
      language?.id ||
      language ||
      1
  );

  // ==========================================================
  // TRANSLATION HELPERS
  // ==========================================================

  const bt = (key, fallback = "") => t(`bookingengine.${key}`, fallback);

  const pt = (key, fallback = "") => t(`personaldtls.${key}`, fallback);

  const rt = (key, fallback = "") => t(`ratechart.${key}`, fallback);

  const et = (key, fallback = "") => t(`extracharges.${key}`, fallback);

  const ot = (key, fallback = "") => t(`others.${key}`, fallback);

  // ==========================================================
  // STATE
  // ==========================================================

  const [booking, setBooking] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [locations, setLocations] = useState([]);

  const [locationsLoading, setLocationsLoading] = useState(false);

  const [freeExtras, setFreeExtras] = useState([]);

  const [paidExtras, setPaidExtras] = useState([]);

  const [freeInsurance, setFreeInsurance] = useState([]);

  const [paidInsurance, setPaidInsurance] = useState([]);

  const [extrasLoading, setExtrasLoading] = useState(false);

  // ==========================================================
  // EMAIL STATE
  // ==========================================================

  const [mailStatus, setMailStatus] = useState("idle");

  const [mailMessage, setMailMessage] = useState("");

  // ==========================================================
  // FEATURES
  // ==========================================================

  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  // ==========================================================
  // BOOKING ID
  // ==========================================================

  const resolveBookingId = () => {
    // 1. React navigation state
    if (location.state?.bookingid) {
      return location.state.bookingid;
    }

    // 2. sessionStorage
    try {
      const stored = sessionStorage.getItem("finalBooking");

      if (stored) {
        const parsed = JSON.parse(stored);

        if (parsed?.bookingid) {
          return parsed.bookingid;
        }
      }
    } catch (error) {
      console.warn("Unable to read finalBooking:", error);
    }

    // 3. URL
    const params = new URLSearchParams(location.search);

    return params.get("bookingid") || null;
  };

  // ==========================================================
  // LOAD LOCATIONS
  // ==========================================================

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocationsLoading(true);

        const response = await api.get("/locations/getLocations", {
          params: {
            city_id: 1,
            lang_id: languageId,
          },
        });

        const result = response.data;

        if (result?.success && Array.isArray(result.data)) {
          setLocations(result.data);
        } else {
          setLocations([]);
        }
      } catch (error) {
        console.error("Location loading error:", error);

        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    loadLocations();
  }, [languageId]);

  // ==========================================================
  // LOAD EXTRAS
  // ==========================================================

  useEffect(() => {
    const loadExtras = async () => {
      try {
        setExtrasLoading(true);

        const response = await api.get("/extras", {
          params: {
            lang_id: languageId,
          },
        });

        const result = response.data;

        if (!result?.success) {
          setFreeExtras([]);
          setPaidExtras([]);
          setFreeInsurance([]);
          setPaidInsurance([]);

          return;
        }

        setFreeExtras(
          Array.isArray(result.freeExtras) ? result.freeExtras : []
        );

        setPaidExtras(
          Array.isArray(result.paidExtras) ? result.paidExtras : []
        );

        setFreeInsurance(
          Array.isArray(result.freeInsurance) ? result.freeInsurance : []
        );

        setPaidInsurance(
          Array.isArray(result.paidInsurance) ? result.paidInsurance : []
        );
      } catch (error) {
        console.error("Extras loading error:", error);

        setFreeExtras([]);
        setPaidExtras([]);
        setFreeInsurance([]);
        setPaidInsurance([]);
      } finally {
        setExtrasLoading(false);
      }
    };

    loadExtras();
  }, [languageId]);

  // ==========================================================
  // GET LOCATION NAME
  // ==========================================================

  const getLocationName = (id) => {
    if (id === undefined || id === null || id === "") {
      return bt("notSelected", "Not selected");
    }

    const found = locations.find(
      (item) => Number(item.loc_id) === Number(id)
    );

    if (found) {
      return found.loc_name;
    }

    return String(id);
  };

  // ==========================================================
  // TIME
  // ==========================================================

  const getTime = (hour, minute) => {
    if (hour === undefined || hour === null || hour === "") {
      return "";
    }

    return `${hour}:${String(minute ?? "00").padStart(2, "0")}`;
  };

  // ==========================================================
  // MAP DATABASE BOOKING
  // ==========================================================

  const mapDbBookingToViewModel = (row) => {
    let sessionExtras = {
      freeExtras: [],
      paidExtras: {},
      freeInsurance: [],
      paidInsurance: {},
    };

    let sessionPaymentMethod = null;

    // Coupon fallback taken from the booking page (same browser session)
    let sessionCoupon = {
      code: "",
      perc: 0,
      amt: 0,
    };

    try {
      const stored = sessionStorage.getItem("finalBooking");

      if (stored) {
        const parsed = JSON.parse(stored);

        if (String(parsed?.bookingid) === String(row?.bookingid)) {
          sessionExtras = {
            freeExtras: parsed?.bookingData?.freeExtras || [],

            paidExtras: parsed?.bookingData?.paidExtras || {},

            freeInsurance: parsed?.bookingData?.freeInsurance || [],

            paidInsurance: parsed?.bookingData?.paidInsurance || {},
          };

          sessionPaymentMethod = parsed?.paymentMethod || null;

          sessionCoupon = {
            code: parsed?.couponCode || "",
            perc: Number(parsed?.couponDiscountPercentage || 0),
            amt: Number(parsed?.couponDiscount || 0),
          };
        }
      }
    } catch (error) {
      console.warn("Unable to read finalBooking:", error);
    }

    const daysFromDates = () => {
      const pick = new Date(row?.online_pickdate);
      const drop = new Date(row?.online_returndate);

      if (Number.isNaN(pick.getTime()) || Number.isNaN(drop.getTime())) {
        return 1;
      }

      return Math.max(1, Math.ceil((drop - pick) / (1000 * 60 * 60 * 24)));
    };

    const rentalDays =
      Number(row?.online_diffdate) > 0
        ? Number(row.online_diffdate)
        : daysFromDates();

    const perDayCarPrice = Number(row?.online_carprice || 0);

    const rentalPrice = perDayCarPrice * rentalDays;

    const pickupCharge = Number(row?.online_locpickupcharge || 0);

    const dropoffCharge = Number(row?.online_locdropoffcharge || 0);

    const oneWayCharge = Number(row?.online_onewayrentalcharges || 0);

    // ------------------------------------------------------
    // COUPON (database first, session as fallback)
    // ------------------------------------------------------

    const couponCode =
      String(row?.Coupon_Code ?? "").trim() || sessionCoupon.code || "";

    const couponPerc =
      Number(row?.Coup_DiscountPerc || 0) || Number(sessionCoupon.perc || 0);

    const couponAmt =
      Number(row?.Coup_DiscountAmt || 0) || Number(sessionCoupon.amt || 0);

    return {
      bookingid: row?.bookingid,

      fullbooking_no: row?.fullbooking_no,

      car: {
        carmodel_name: row?.carmodel_name,

        carmodel_image: row?.carmodel_image,

        cat_name: row?.cat_name,

        carmodel_passgrno: row?.carmodel_passgrno,

        carmodel_luggno: row?.carmodel_luggno,

        transmission: row?.transmission || row?.gear,

        gear: row?.gear,

        airco: row?.airco,

        air_condition: row?.air_condition,

        pricePerDay: perDayCarPrice,
      },

      customer: {
        title: row?.online_title,

        firstName: row?.online_firstname,

        lastName: row?.online_lastname,

        email: row?.online_emailid || row?.online_email,

        phone: row?.online_mobileno || row?.online_telephone,

        mobile: row?.online_mobileno,

        address: row?.online_address,

        city: row?.online_city,

        country: row?.online_country,

        postcode: row?.online_postcode,

        remarks: row?.online_remarks || "",

        dropoffRemarks:
          row?.online_dropremarks || row?.online_dropoffremarks || "",

        DropDetails: row?.DropDetails,
        regualclientid: row?.online_clientid,
        PickDetails: row?.PickDetails,
      },

      bookingCriteria: {
        cmbPickLocation: row?.online_pickstation,

        cmbDropLocation: row?.online_returnstation,

        pickdate: row?.online_pickdate,

        returndate: row?.online_returndate,

        cmbpickHour: row?.online_picktime
          ? String(row.online_picktime).split(":")[0]
          : "",

        cmbpickmin: row?.online_picktime
          ? String(row.online_picktime).split(":")[1]
          : "",

        cmbreturnHour: row?.online_returntime
          ? String(row.online_returntime).split(":")[0]
          : "",

        cmbreturnmin: row?.online_returntime
          ? String(row.online_returntime).split(":")[1]
          : "",
      },

      rentalDays,

      rentalPrice,

      pickupCharge,

      dropoffCharge,

      oneWayCharge,

      rentalTotal: rentalPrice + pickupCharge + dropoffCharge + oneWayCharge,

      paidExtrasTotal: Number(row?.online_totaloptextras || 0),

      paidInsuranceTotal: Number(row?.online_totalinsurance || 0),

      finalTotal: Number(row?.online_onlineprice || 0),

      // COUPON
      couponCode,

      couponPerc,

      couponAmt,

      freeExtras: sessionExtras.freeExtras,

      paidExtras: sessionExtras.paidExtras,

      freeInsurance: sessionExtras.freeInsurance,

      paidInsurance: sessionExtras.paidInsurance,

      paymentMethod: sessionPaymentMethod || "paylater",
    };
  };

  // ==========================================================
  // SEND CONFIRMATION MAIL
  // ==========================================================

  const sendConfirmationMail = async (bookingid) => {
    try {
      setMailStatus("sending");

      setMailMessage(
        bt(
          "sendingConfirmationEmail",
          "Sending booking confirmation email..."
        )
      );

      const response = await api.post("/bookings/send-confirmation-mail", {
        bookingid,
      });

      const result = response.data;

      if (!result?.success) {
        throw new Error(result?.message || "Unable to send email.");
      }

      if (result.alreadySent) {
        setMailStatus("alreadySent");

        setMailMessage(
          bt(
            "confirmationEmailAlreadySent",
            "Booking confirmation email has already been sent."
          )
        );
      } else {
        setMailStatus("sent");

        setMailMessage(
          bt(
            "confirmationEmailSent",
            "Booking confirmation email has been sent successfully."
          )
        );
      }
    } catch (error) {
      console.error("Confirmation mail error:", error);

      setMailStatus("failed");

      setMailMessage(
        error?.response?.data?.message ||
          error?.message ||
          bt(
            "bookingConfirmedEmailFailed",
            "Booking is confirmed, but email could not be sent."
          )
      );
    }
  };

  // ==========================================================
  // FETCH BOOKING
  // ==========================================================

  useEffect(() => {
    const bookingid = resolveBookingId();

    if (!bookingid) {
      setError(
        bt("bookingReferenceNotFound", "Booking reference not found.")
      );

      setLoading(false);

      return;
    }

    const fetchBooking = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post("/bookings/details", {
          bookingid,
        });

        const result = response.data;

        if (!result || !result.success) {
          setError(
            result?.message ||
              bt(
                "unableToLoadBookingConfirmation",
                "Unable to load booking confirmation."
              )
          );

          return;
        }

        const mappedBooking = mapDbBookingToViewModel(result.data);

        setBooking(mappedBooking);

        await sendConfirmationMail(bookingid);
      } catch (error) {
        console.error("Confirmation error:", error);

        setError(
          error?.response?.data?.message ||
            bt(
              "unableToLoadBookingConfirmation",
              "Unable to load booking confirmation."
            )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [navigate, location.state, location.search]);

  // ==========================================================
  // CAR IMAGE
  // ==========================================================

  const getCarImage = (image) => {
    if (!image) {
      return "";
    }

    let imagePath = String(image).trim();

    if (!imagePath) {
      return "";
    }

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    imagePath = imagePath.replace(/^\/+/, "");

    const imageBase = IMAGE_BASE_URL || "";

    if (imagePath.startsWith("images/")) {
      return `${imageBase}/${imagePath}`;
    }

    if (imagePath.startsWith("CarImage/")) {
      return `${imageBase}/images/${imagePath}`;
    }

    return `${imageBase}/images/CarImage/${imagePath}`;
  };

  // ==========================================================
  // SELECTED EXTRA CHECK
  // ==========================================================

  const isSelectedId = (collection, id) => {
    if (Array.isArray(collection)) {
      return collection.some((item) => Number(item) === Number(id));
    }

    if (collection && typeof collection === "object") {
      return Number(collection[id] || 0) > 0;
    }

    return false;
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>
          {bt("loadingBookingConfirmation", "Loading booking confirmation...")}
        </h4>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>{error}</h4>

        <button
          type="button"
          className="nextbutton1 nextbutton"
          style={{
            border: "none",
            cursor: "pointer",
            marginTop: "20px",
          }}
          onClick={() => navigate("/bookacar")}
        >
          {bt("startNewBooking", ot("others6", "New reservation"))}
        </button>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  // ==========================================================
  // VALUES
  // ==========================================================

  const car = booking.car || {};

  const customer = booking.customer || {};

  const criteria = booking.bookingCriteria || {};

  const rentalDays = Number(booking.rentalDays || 1);

  const rentalPrice = Number(booking.rentalPrice || 0);

  const perdaycarprice = Number(car.pricePerDay || 0);

  const pickupCharge = Number(booking.pickupCharge || 0);

  const dropoffCharge = Number(booking.dropoffCharge || 0);

  const oneWayCharge = Number(booking.oneWayCharge || 0);

  const rentalTotal = Number(booking.rentalTotal || 0);

  const paidExtrasTotal = Number(booking.paidExtrasTotal || 0);

  const paidInsuranceTotal = Number(booking.paidInsuranceTotal || 0);

  const finalTotal = Number(booking.finalTotal || 0);

  // COUPON
  const couponCode = booking.couponCode || "";

  const couponPerc = Number(booking.couponPerc || 0);

  const couponAmt = Number(booking.couponAmt || 0);

  const fullBookingNo = booking.fullbooking_no || booking.bookingid;

  const carName = car.carmodel_name || bt("selectedCar", "Selected car");

  const category = car.cat_name || "";

  const carImage = getCarImage(car.carmodel_image);

  const passengers = car.carmodel_passgrno || 0;

  const luggage = car.carmodel_luggno || 0;

  const transmission =
    car.transmission || car.gear || bt("manual", "Manual");

  const airCondition = car.airco || car.air_condition || "Air-Co";

  // ==========================================================
  // FEATURES
  // ==========================================================

  const features = [
    bt("noExtraCost", "No Extra Cost"),

    bt("allInclusive", "All inclusive"),

    bt("noGuarantee", "No guarantee"),

    bt("noCreditCardRequired", "No credit card required"),

    bt("freeRoadmap", "Free roadmap of Crete"),

    bt("unlimitedMileage", "Unlimited mileage"),

    bt("roadAssistance", "24/7 road assistance"),

    bt("fullInsurance", "Full insurance"),
  ];

  const mainFeatures = features.slice(0, 4);

  const extraFeatures = features.slice(4);

  // ==========================================================
  // EXTRAS
  // ==========================================================

  const paidExtrasSelection = booking.paidExtras || {};

  const freeExtrasSelection = booking.freeExtras || {};

  const paidInsuranceSelection = booking.paidInsurance || {};

  const freeInsuranceSelection = booking.freeInsurance || {};

  // ==========================================================
  // ACTIVE PAID EXTRAS
  // ==========================================================

  const activePaidExtras = paidExtras
    .filter((item) => {
      const id = Number(item.optdet_id ?? item.optitem_id);

      return isSelectedId(paidExtrasSelection, id);
    })
    .map((item) => {
      const id = Number(item.optdet_id ?? item.optitem_id);

      let quantity = 1;

      if (
        paidExtrasSelection &&
        typeof paidExtrasSelection === "object" &&
        !Array.isArray(paidExtrasSelection)
      ) {
        quantity = Number(paidExtrasSelection[id] || 1);
      }

      const dayRate = Number(
        item.dayRate ??
          item.day_rate ??
          item.ratePerDay ??
          item.rate_per_day ??
          0
      );

      let baseTotal = dayRate * rentalDays;

      const maxRate = Number(item.maxRate || 0);

      if (maxRate > 0 && baseTotal > maxRate) {
        baseTotal = maxRate;
      }

      return {
        ...item,

        id,

        quantity,

        dayRate,

        total: baseTotal * quantity,
      };
    });

  // ==========================================================
  // ACTIVE FREE EXTRAS
  // ==========================================================

  const activeFreeExtras = freeExtras.filter((item) => {
    const id = Number(item.optdet_id ?? item.optitem_id);

    return isSelectedId(freeExtrasSelection, id);
  });

  // ==========================================================
  // ACTIVE PAID INSURANCE
  // ==========================================================

  const activePaidInsurance = paidInsurance
    .filter((item) => {
      const id = Number(item.insdtl_id ?? item.insitem_id);

      return isSelectedId(paidInsuranceSelection, id);
    })
    .map((item) => {
      const id = Number(item.insdtl_id ?? item.insitem_id);

      const ratePerDay = Number(
        item.chargePerDay ||
          item.ratePerDay ||
          item.rate_per_day ||
          item.online_itemrate ||
          0
      );

      return {
        ...item,

        id,

        ratePerDay,

        total: ratePerDay * rentalDays,
      };
    });

  // ==========================================================
  // ACTIVE FREE INSURANCE
  // ==========================================================

  const activeFreeInsurance = freeInsurance.filter((item) => {
    const id = Number(item.insitem_id ?? item.insdtl_id);

    return isSelectedId(freeInsuranceSelection, id);
  });

  // ==========================================================
  // CALCULATED TOTALS
  // ==========================================================

  const calculatedPaidExtrasTotal = activePaidExtras.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  const calculatedPaidInsuranceTotal = activePaidInsurance.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  /*
   * Prefer database final total when it exists.
   * Otherwise calculate from the displayed components,
   * minus the coupon discount.
   */

  const calculatedFinalTotal = Math.max(
    0,
    rentalTotal +
      calculatedPaidExtrasTotal +
      calculatedPaidInsuranceTotal -
      couponAmt
  );

  const displayFinalTotal = finalTotal > 0 ? finalTotal : calculatedFinalTotal;

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="p20">
      <div className="container container2 p0">
        <div
          className="col-md-12 main-content"
          style={{
            float: "left",
          }}
        >
          {/* ==================================================
              THANK YOU
          ================================================== */}

          <div className="booking-card">
            <div className="card-header">
              <h3 style={{ color: "#119b6e" }}>
                {ot("others5", "Thank you for your reservation")}
              </h3>

              <p
                style={{
                  marginTop: "6px",
                }}
              >
                {ot("others17", "Your booking reference number Is")}

                {" : "}

                <strong
                  style={{
                    color: "#ffffff",
                    background: "#119b6e",
                    padding: "2px 12px",
                    borderRadius: "13px",
                  }}
                >
                  {fullBookingNo}
                </strong>
              </p>
            </div>

            <div
              className="conform1"
              style={{
                width: "80%",
                float: "left",
              }}
            >
              <p>
                {ot("others10", "Dear")} {customer.title} {customer.firstName}{" "}
                {customer.lastName},
              </p>

              <p>
                {ot(
                  "others11",
                  "Thank you for your booking. Your reservation has now been placed. Show this voucher in print or save it on your mobile device at receipt of the car. Your booking reference number is :"
                )}{" "}
                <strong style={{ color: "#119b6e" }}>{fullBookingNo}</strong>
              </p>

              <p>
                {ot(
                  "others12",
                  "Please keep this number for future reference. This reservation includes all taxes, unlimited mileage and a map of Crete."
                )}
              </p>
            </div>

            {/* PRINT */}

            <div
              style={{
                width: "100px",
                float: "right",
                textAlign: "center",
              }}
            >
              <div
                onClick={() => window.print()}
                style={{
                  cursor: "pointer",
                }}
              >
                <img
                  src={printImage}
                  alt={ot("others7", "Print preview")}
                  style={{
                    display: "block",
                    margin: "0 auto",
                    width: "50px",
                    height: "50px",
                  }}
                />
              </div>

              <div className="tbltxtnew">
                {ot("others7", "Print preview")}
              </div>
            </div>

            {/* NEW RESERVATION */}

            <div
              style={{
                width: "100px",
                float: "right",
                textAlign: "center",
              }}
            >
              <div
                onClick={() => navigate("/bookacar")}
                style={{
                  cursor: "pointer",
                }}
              >
                <img
                  src={newReservationImage}
                  alt={ot("others6", "New reservation")}
                  style={{
                    display: "block",
                    margin: "0 auto",
                    width: "50px",
                    height: "50px",
                  }}
                />
              </div>

              <div
                className="tbltxtnew"
                onClick={() => navigate("/bookacar")}
                style={{
                  cursor: "pointer",
                }}
              >
                {ot("others6", "New reservation")}
              </div>
            </div>

            <div className="clearfix" />
          </div>

          {/* ==================================================
              CAR
          ================================================== */}

          <div className="rental-card">
            <div className="card-main-content">
              <div
                className="col-md-9"
                style={{
                  float: "left",
                }}
              >
                <h2 className="car-title">
                  {carName} <span>({bt("orSimilar", "or similar")})</span>
                </h2>

                <div className="car-subtitle">{category}</div>
              </div>

              <div
                className="col-md-3"
                style={{
                  float: "left",
                }}
              >
                <div className="price-container">
                  <div className="price-label">
                    {bt("finalPrice", "Final Price")}
                  </div>

                  <div className="main-price">
                    € {displayFinalTotal.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="clearfix" />

              {/* CAR IMAGE */}

              <div
                className="col-md-4"
                style={{
                  float: "left",
                }}
              >
                <div className="image-container">
                  {carImage && (
                    <img
                      src={carImage}
                      alt={carName}
                      className="car-img"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                </div>
              </div>

              {/* FEATURES */}

              <div
                className="col-md-4"
                style={{
                  float: "left",
                }}
              >
                <div className="features-container">
                  <div className="specs-row">
                    <span className="spec-tag">👤 {passengers}</span>

                    <span className="spec-tag">🧳 {luggage}</span>

                    <span className="spec-tag">❄️ {airCondition}</span>

                    <span className="spec-tag">⚙️ {transmission}</span>
                  </div>

                  {mainFeatures.map((feature, index) => (
                    <div
                      className="feature-item"
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <img
                        src={tickImage}
                        alt="Included"
                        style={{
                          width: "20px",
                          height: "20px",
                          marginRight: "8px",
                        }}
                      />

                      <strong>{feature}</strong>
                    </div>
                  ))}

                  {extraFeatures.length > 0 && (
                    <div
                      style={{
                        marginTop: "10px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setFeaturesExpanded((prev) => !prev)}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#087cff",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        {featuresExpanded
                          ? bt("showLess", "Show less")
                          : bt(
                              "showMore",
                              `Show ${extraFeatures.length} More`
                            )}
                      </button>

                      {featuresExpanded && (
                        <div
                          style={{
                            marginTop: "10px",
                          }}
                        >
                          {extraFeatures.map((feature, index) => (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "8px",
                              }}
                            >
                              <img
                                src={tickImage}
                                alt="Included"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  marginRight: "8px",
                                }}
                              />

                              <strong>{feature}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* NOTE */}

              <div
                className="col-md-4"
                style={{
                  float: "left",
                }}
              >
                <div className="actions-container">
                  <div className="car-d notice">
                    {bt(
                      "minimumDriverAgeNote",
                      "Note: The minimum driver age for this car is 21."
                    )}
                  </div>
                </div>
              </div>

              <div className="clearfix" />
            </div>
          </div>

          {/* ==================================================
              ITINERARY
          ================================================== */}

          <div className="l-box">
            <div className="extra-details">
              {bt("yourItinerary", "Your itinerary")}

              {" - "}

              {bt("totalDaysOfHire", "Total days of hire")} {rentalDays}{" "}
              {bt("days", "day(s)")}
            </div>

            <div className="car-box price-box">
              <table className="table word-break" width="100%">
                <tbody>
                  <tr>
                    <td>
                      <strong>
                        {bt("pickUpLocation", "Pick up location")}
                      </strong>

                      {" : "}

                      {getLocationName(criteria.cmbPickLocation)}
                    </td>

                    <td>
                      <strong>{bt("pickUpDate", "Pick up date")}</strong>

                      {" : "}

                      {criteria.pickdate}
                    </td>

                    <td>
                      <strong>{bt("pickUpTime", "Pick up time")}</strong>

                      {" : "}

                      {getTime(criteria.cmbpickHour, criteria.cmbpickmin)}
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>{bt("returnLocation", "Return location")}</strong>

                      {" : "}

                      {getLocationName(criteria.cmbDropLocation)}
                    </td>

                    <td>
                      <strong>{bt("returnDate", "Return date")}</strong>

                      {" : "}

                      {criteria.returndate}
                    </td>

                    <td>
                      <strong>{bt("returnTime", "Return time")}</strong>

                      {" : "}

                      {getTime(criteria.cmbreturnHour, criteria.cmbreturnmin)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ==================================================
              RATE CHART
          ================================================== */}

          <div className="rate">{rt("ratechart4", "Rate charts")}</div>

          <div className="price-box">
            <table
              className="table word-break"
              width="100%"
              style={{
                marginBottom: 0,
              }}
            >
              <thead>
                <tr>
                  <th
                    className="optionalextra"
                    style={{
                      width: "35%",
                    }}
                  >
                    {rt("quote", "Quote")}
                  </th>

                  <th
                    className="optionalextra"
                    style={{
                      width: "35%",
                      textAlign: "center",
                    }}
                  >
                    {rt("calculation", "Calculation")}
                  </th>

                  <th
                    className="optionalextra"
                    style={{
                      width: "30%",
                      textAlign: "right",
                    }}
                  >
                    {rt("total", "Total")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* RENTAL PRICE */}

                <tr>
                  <td>{rt("rentalPrice", "Rental price")}</td>

                  <td
                    style={{
                      textAlign: "center",
                      color: "#0a9311",
                      fontWeight: "600",
                    }}
                  >
                    {rentalDays}

                    {" × €"}

                    {(rentalDays > 0 ? perdaycarprice : rentalPrice).toFixed(2)}
                  </td>

                  <td
                    style={{
                      textAlign: "right",
                      color: "#0a9311",
                      fontWeight: "600",
                    }}
                  >
                    € {rentalPrice.toFixed(2)}
                  </td>
                </tr>

                {/* PICKUP */}

                {pickupCharge > 0 && (
                  <tr>
                    <td>{rt("pickupCharge", "Pickup charge")}</td>

                    <td />

                    <td
                      style={{
                        textAlign: "right",
                      }}
                    >
                      € {pickupCharge.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* DROPOFF */}

                {dropoffCharge > 0 && (
                  <tr>
                    <td>{rt("dropoffCharge", "Dropoff charge")}</td>

                    <td />

                    <td
                      style={{
                        textAlign: "right",
                      }}
                    >
                      € {dropoffCharge.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* ONE WAY */}

                {oneWayCharge > 0 && (
                  <tr>
                    <td>{rt("ratechart19", "One way rental charges")}</td>

                    <td />

                    <td
                      style={{
                        textAlign: "right",
                      }}
                    >
                      € {oneWayCharge.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* RENTAL TOTAL */}

                <tr>
                  <td>
                    <strong>
                      {rt("totalRentalPrice", "Total rental price")}
                    </strong>
                  </td>

                  <td />

                  <td
                    style={{
                      textAlign: "right",
                      color: "#8a74dd",
                      fontWeight: "600",
                    }}
                  >
                    € {rentalTotal.toFixed(2)}
                  </td>
                </tr>

                {/* OPTIONAL EXTRAS HEADING */}

                {activePaidExtras.length > 0 && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        fontWeight: "600",
                        paddingTop: "12px",
                      }}
                    >
                      {et("extracharges2", "Optional extras")}
                    </td>
                  </tr>
                )}

                {/* OPTIONAL EXTRAS */}

                {activePaidExtras.map((item) => {
                  const itemName =
                    item.name ||
                    item.optitem_name ||
                    item.optdet_name ||
                    et("optionalExtra", "Optional extra");

                  const dayRate = Number(item.dayRate || 0);

                  const quantity = Number(item.quantity || 1);

                  const itemTotal = Number(item.total || 0);

                  return (
                    <tr key={`rate-extra-${item.id}`}>
                      <td>{itemName}</td>

                      <td
                        style={{
                          textAlign: "center",
                          color: "#8a74dd",
                          fontWeight: "600",
                        }}
                      >
                        €{dayRate.toFixed(2)}

                        {" × "}

                        {rentalDays}

                        {" × "}

                        {quantity}
                      </td>

                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: "600",
                          color: "#8a74dd",
                        }}
                      >
                        € {itemTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}

                {/* OPTIONAL EXTRAS TOTAL */}

                {activePaidExtras.length > 0 && (
                  <tr>
                    <td>
                      <strong>
                        {et("totalExtraCharges", "Total extra charges")}
                      </strong>
                    </td>

                    <td />

                    <td
                      style={{
                        textAlign: "right",
                        color: "#ff6600",
                        fontWeight: "600",
                      }}
                    >
                      € {calculatedPaidExtrasTotal.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* PAID INSURANCE */}

                {activePaidInsurance.length > 0 && (
                  <tr>
                    <td>
                      <strong>{et("paidInsurance", "Paid insurance")}</strong>
                    </td>

                    <td />

                    <td
                      style={{
                        textAlign: "right",
                      }}
                    >
                      € {calculatedPaidInsuranceTotal.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* COUPON DISCOUNT */}

                {couponAmt > 0 && (
                  <tr>
                    <td
                      style={{
                        color: "#0a9311",
                        fontWeight: "600",
                      }}
                    >
                      Coupon Discount
                      {couponCode && (
                        <span style={{ fontWeight: "400" }}> ({couponCode})</span>
                      )}
                      {couponPerc > 0 && (
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "400",
                            marginLeft: "8px",
                          }}
                        >
                          ({couponPerc}%)
                        </span>
                      )}
                    </td>

                    <td />

                    <td
                      style={{
                        textAlign: "right",
                        color: "#0a9311",
                        fontWeight: "600",
                      }}
                    >
                      - € {couponAmt.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* FINAL TOTAL */}

                <tr>
                  <td style={{ background: "#edf7ec" }}>
                    <strong>
                      {rt("finalTotalRentalPrice", "Final total rental price")}
                    </strong>
                  </td>

                  <td style={{ background: "#edf7ec" }}></td>

                  <td
                    style={{
                      background: "#edf7ec",
                      textAlign: "right",
                      fontSize: "18px",
                      fontWeight: "700",
                    }}
                  >
                    € {displayFinalTotal.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ==================================================
              EXTRA DETAILS
          ================================================== */}

          {activePaidExtras.length > 0 && (
            <>
              <div className="extra-details">
                {et("extracharges", "Extra details")}
                {" : "}
              </div>

              <div
                className="extras car-box price-box"
                style={{
                  padding: 0,
                  marginBottom: "15px",
                }}
              >
                <table
                  className="table"
                  width="100%"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <thead>
                    <tr>
                      <th className="optionalextra">{et("items", "Items")}</th>

                      <th className="optionalextra">
                        {et("ratePerDay", "Rate per day")}
                      </th>

                      <th className="optionalextra">
                        {et("selection", "Selection")}
                      </th>

                      <th className="optionalextra">{et("total", "Total")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {activePaidExtras.map((item) => {
                      const itemName =
                        item.name ||
                        item.optitem_name ||
                        item.optdet_name ||
                        et("optionalExtra", "Optional extra");

                      const dayRate = Number(item.dayRate || 0);

                      const quantity = Number(item.quantity || 1);

                      const itemTotal = Number(item.total || 0);

                      return (
                        <tr key={`extra-detail-${item.id}`}>
                          <td>{itemName}</td>

                          <td>
                            €{dayRate.toFixed(2)}
                            {" / "}
                            {et("day", "Day")}
                          </td>

                          <td>{quantity}</td>

                          <td>
                            <strong>€{itemTotal.toFixed(2)}</strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ==================================================
              FREE EXTRAS
          ================================================== */}

          {activeFreeExtras.length > 0 && (
            <>
              <div className="extra-details">
                {et("extracharges1", "Free optional extras")}
              </div>

              <div className="extras car-box price-box">
                <table className="table" width="100%">
                  <tbody>
                    {activeFreeExtras.map((item) => (
                      <tr key={item.optdet_id ?? item.optitem_id}>
                        <td>
                          {item.name ||
                            item.optitem_name ||
                            item.optdet_name ||
                            ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ==================================================
              INSURANCE
          ================================================== */}

          <div className="extra-details">{et("extracharges7", "Insurances")}</div>

          {/* PAID INSURANCE */}

          {activePaidInsurance.length > 0 && (
            <div className="extras car-box price-box">
              <table className="table" width="100%">
                <thead>
                  <tr>
                    <th>{et("item", "Item")}</th>

                    <th>{et("ratePerDay", "Rate per day")}</th>

                    <th>{rt("total", "Total")}</th>
                  </tr>
                </thead>

                <tbody>
                  {activePaidInsurance.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.name ||
                          item.insitem_name ||
                          item.insdtl_name ||
                          ""}
                      </td>

                      <td>
                        €{Number(item.ratePerDay || 0).toFixed(2)}
                        {" / "}
                        {bt("day", "day")}
                      </td>

                      <td>
                        <strong>€{Number(item.total || 0).toFixed(2)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* FREE INSURANCE */}

          {activeFreeInsurance.length > 0 && (
            <div className="extras car-box price-box">
              <table className="table" width="100%">
                <tbody>
                  {activeFreeInsurance.map((item) => (
                    <tr key={item.insitem_id ?? item.insdtl_id}>
                      <td>
                        {item.name ||
                          item.insitem_name ||
                          item.insdtl_name ||
                          ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ==================================================
              PERSONAL DETAILS
          ================================================== */}

          <div className="extra-details">
            {pt("personaldtls0", "Personal details")}
          </div>

          <div className="extras car-box price-box">
            <table className="table" width="100%">
              <tbody>
                {/* ROW 1 */}

                <tr>
                  <td>
                    <strong>{pt("personaldtls2", "First name")}</strong>

                    <br />

                    {customer.title} {customer.firstName}
                  </td>

                  <td>
                    <strong>{pt("personaldtls3", "Family name")}</strong>

                    <br />

                    {customer.lastName}
                  </td>

                  <td>
                    <strong>{pt("personaldtls8", "Mobile no")}</strong>

                    <br />

                    {customer.mobile || customer.phone}
                  </td>

                  <td>
                    <strong>{pt("personaldtls4", "Email ID")}</strong>

                    <br />

                    {customer.email}
                  </td>
                </tr>

                {/* ROW 2 */}

                <tr>
                  <td>
                    <strong>{pt("personaldtls14", "Home Address")}</strong>

                    <br />

                    {customer.address}
                  </td>

                  <td>
                    <strong>{bt("pickUpLocation", "Pick up location")}</strong>

                    <br />

                    {getLocationName(criteria.cmbPickLocation)}
                  </td>

                  <td>
                    <strong>{pt("personaldtls16", "Zip code")}</strong>

                    <br />

                    {customer.postcode}
                  </td>

                  <td>
                    <strong>
                      {bt("dropOffLocation", "Drop off location")}
                    </strong>

                    <br />

                    {getLocationName(criteria.cmbDropLocation)}
                  </td>
                </tr>

                {/* ROW 3 */}

                <tr>
                  <td>
                    <strong>{pt("personaldtls15", "City")}</strong>

                    <br />

                    {customer.city}
                  </td>

                  <td>
                    <strong>{pt("personaldtls10", "Country")}</strong>

                    <br />

                    {customer.country}
                  </td>

                  <td>
                    <strong>
                      {pt("personaldtls23", "Pick up comments")}
                    </strong>

                    <br />

                    {customer.PickDetails}
                  </td>

                  <td>
                    <strong>
                      {pt("personaldtls24", "Drop off comments")}
                    </strong>

                    <br />

                    {customer.DropDetails}
                  </td>
              
                </tr>
                <tr>
                <td>
                    <strong>
                      {/* {pt("personaldtls24", "RagualClient")} */}
                      Ragual Client
                    </strong>

                    <br />

                    {customer.regualclientid}
                  </td>
                </tr>

              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfirmBooking;
